import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, ChevronDown, Sparkles, Edit3 } from "lucide-react";
import TaxPill from "../../../ui/TaxPill";
import { useItemPicker } from "../hooks/useItemPicker";
import { formatCurrency } from "../../../utils/formatCurrency";

export default function LineItemsEditor({ invoice, updateField, disabled = false, showValidation = false }) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const hasItems = invoice.items && invoice.items.length > 0;
  const showError = showValidation && !hasItems;

  const addItem = () => {
    if (disabled) return;
    const itemType = invoice.type === "goods" ? "product" : "service";
    const newItem = {
      id: Date.now(),
      name: "",
      type: itemType,
      quantity: 1,
      rate: 0,
      discount: 0,
      discountType: "percentage",
      gstRate: 18,
      gstType: "cgst_sgst",
      description: "",
      hsn: itemType === "product" ? "" : null,
      sac: itemType === "service" ? "" : null,
      uom: itemType === "product" ? "pcs" : "hrs", // Default unit based on type
      billingFrequency: itemType === "service" ? "per_month" : null, // Billing frequency only for services
      sourceItemId: null,
      isNew: true,
      isOverridden: false,
    };

    updateField("items", [...(invoice.items || []), newItem]);
    setExpandedIndex((invoice.items || []).length);
  };

  const updateItem = (index, value) => {
    const newItems = [...(invoice.items || [])];
    const currentItem = newItems[index];

    newItems[index] =
      typeof value === "function"
        ? value(currentItem)
        : {
            ...currentItem,
            ...value,
            // Track if editing existing item
            isOverridden:
              currentItem.sourceItemId &&
              (value.rate !== undefined ||
                value.gstRate !== undefined ||
                value.hsn !== undefined ||
                value.sac !== undefined ||
                value.uom !== undefined),
          };

    updateField("items", newItems);
  };

  const removeItem = (index) => {
    updateField(
      "items",
      invoice.items.filter((_, i) => i !== index)
    );
    setExpandedIndex(null);
  };

  const items = invoice.items || [];

  return (
    <section className="space-y-3.5" data-validation-target="items">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">Line items</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Search catalog or create new items inline
          </p>
        </div>
        {!disabled && (
          <button
            onClick={addItem}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-all duration-150 hover:shadow-sm active:scale-95"
          >
            <Plus size={16} />
            Add item
          </button>
        )}
      </div>

      {items.length === 0 ? (
        disabled ? (
          <div className="border border-slate-200/60 rounded-xl p-12 text-center bg-slate-50/50 shadow-sm">
            <div className="text-sm text-slate-500">No line items</div>
          </div>
        ) : (
          <EmptyState onAdd={addItem} showError={showError} />
        )
      ) : (
        <div className="space-y-2.5">
          {items.map((item, index) => (
            <LineItem
              key={item.id}
              item={item}
              index={index}
              invoice={invoice}
              expanded={expandedIndex === index}
              onExpand={() => !disabled && setExpandedIndex(index)}
              onCollapse={() => setExpandedIndex(null)}
              updateItem={updateItem}
              updateField={updateField}
              removeItem={() => removeItem(index)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* ───────────────────────────── */

function LineItem({
  item,
  index,
  invoice,
  expanded,
  onExpand,
  onCollapse,
  updateItem,
  updateField,
  removeItem,
  disabled = false,
}) {
  const base = (item.quantity || 0) * (item.rate || 0);
  const discount =
    item.discountType === "percentage"
      ? (base * (item.discount || 0)) / 100
      : item.discount || 0;

  const amountAfterDiscount = Math.max(0, base - discount);
  const gstRate = item.gstRate || 0;
  const taxAmount = (amountAfterDiscount * gstRate) / 100;

  // Determine GST type based on client state
  const clientState = invoice.client?.state || "Maharashtra";
  const isSameState = clientState === "Maharashtra";
  const gstType = isSameState ? "cgst_sgst" : "igst";

  // Calculate GST split
  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;

  if (gstType === "cgst_sgst") {
    cgstAmount = taxAmount / 2;
    sgstAmount = taxAmount / 2;
  } else {
    igstAmount = taxAmount;
  }

  const totalAmount = amountAfterDiscount + taxAmount;

  const [search, setSearch] = useState(item.name || "");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const dropdownRef = useRef(null);

  // Determine item type based on invoice type (goods -> product, services -> service)
  const itemTypeFilter = invoice.type === "goods" ? "product" : "service";
  const { items: suggestions } = useItemPicker(search, itemTypeFilter);

  useEffect(() => setSearch(item.name || ""), [item.name]);
  useEffect(() => setHighlighted(-1), [suggestions]);
  useEffect(() => {
    if (!expanded) {
      setOpen(false);
      setHighlighted(-1);
    }
  }, [expanded]);

  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const applyItem = (s) => {
    // Determine item type from catalog item or invoice
    const catalogType = s.type || (invoice.type === "goods" ? "product" : "service");
    
    // Auto-update invoice type to match the selected item type
    const invoiceType = catalogType === "product" ? "goods" : "services";
    if (invoice.type !== invoiceType) {
      updateField("type", invoiceType);
    }
    
    updateItem(index, {
      id: item.id,
      name: s.name,
      type: catalogType,
      quantity: 1,
      rate: Number(s.price ?? s.rate ?? 0),
      discount: 0,
      discountType: "percentage",
      gstRate: Number(s.tax ?? s.tax_rate ?? 18),
      gstType: s.gst_type || "cgst_sgst",
      description: s.description ?? "",
      hsn: catalogType === "product" ? (s.hsn ?? "") : "",
      sac: catalogType === "service" ? (s.sac ?? "") : "",
      uom: s.uom ?? "",
      sourceItemId: s.id,
      isNew: false,
      isOverridden: false,
    });

    setSearch(s.name);
    setOpen(false);
    setHighlighted(-1);
  };

  // Handle inline creation - if name doesn't match, create new item
  const handleNameChange = (value) => {
    setSearch(value);
    updateItem(index, { name: value, isNew: !suggestions.some((s) => s.name.toLowerCase() === value.toLowerCase()) });
    setOpen(value.length > 0);
  };

  const onKeyDown = (e) => {
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlighted >= 0 && suggestions[highlighted]) {
      e.preventDefault();
      applyItem(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  // Determine item type for HSN/SAC display
  // Allow item-level override, fallback to invoice type
  const itemType = item.type || (invoice.type === "goods" ? "product" : "service");

  /* ───── COLLAPSED ───── */
  if (!expanded) {
    return (
      <div
        onClick={onExpand}
        className={`group relative flex justify-between items-center px-4 py-3 rounded-lg bg-white transition-all duration-200 shadow-sm ${
          disabled
            ? "border border-slate-200/60 cursor-pointer"
            : "border border-transparent hover:border-indigo-200/60 hover:shadow-md hover:-translate-y-[1px] cursor-pointer"
        }`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-medium text-slate-900 group-hover:text-indigo-600 transition-colors duration-150 truncate">
                {item.name || <span className="text-slate-400 group-hover:text-indigo-500">Click to add item</span>}
              </div>
              {item.isNew && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100">
                  <Sparkles size={10} />
                  New
                </span>
              )}
              {item.isOverridden && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100">
                  <Edit3 size={10} />
                  Modified
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {item.type === 'service' && item.sac && `SAC ${item.sac} • `}
              {item.type === 'product' && item.hsn && `HSN ${item.hsn} • `}
              {item.quantity} {item.uom || 'Nos'} × {formatCurrency(item.rate)}
              {item.billingFrequency && item.billingFrequency !== 'one_time' && ` / ${formatBillingFrequency(item.billingFrequency)}`}
              {item.discount > 0 && ` • ${item.discountType === 'percentage' ? `${item.discount}%` : formatCurrency(item.discount)} off`}
              {item.description && ` • ${item.description.substring(0, 40)}${item.description.length > 40 ? "..." : ""}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          {gstRate > 0 && (
            <div className="text-[10px] text-slate-400 font-medium">
              +{gstRate}% GST
            </div>
          )}
          <div className="text-base font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors duration-150 tabular-nums">
            {formatCurrency(totalAmount)}
          </div>
          <ChevronDown size={16} className="text-slate-400 group-hover:text-indigo-500 transition-colors duration-150" />
        </div>
      </div>
    );
  }

  /* ───── EXPANDED ───── */
  return (
    <div className="rounded-xl p-6 space-y-5 bg-white border border-indigo-200/40 shadow-lg transition-all duration-200 animate-in fade-in slide-in-from-top-2">
      {/* Item Name Search/Create */}
      <div className="relative" ref={dropdownRef}>
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              autoFocus={!disabled}
              value={search}
              onChange={(e) => handleNameChange(e.target.value)}
              onFocus={() => !disabled && setOpen(search.length > 0)}
              onKeyDown={onKeyDown}
              placeholder="Search catalog or type new item name..."
              disabled={disabled}
              className="w-full px-3 py-2 text-[15px] font-medium bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 focus:shadow-sm transition-all disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
            />
            {search.length > 0 && !item.sourceItemId && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-medium">
                Press Enter to create
              </div>
            )}
          </div>
          {item.isNew && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100 shrink-0">
              <Sparkles size={12} />
              New item
            </span>
          )}
          {item.isOverridden && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-600 border border-amber-100 shrink-0">
              <Edit3 size={12} />
              Modified
            </span>
          )}
        </div>

        {open && suggestions.length > 0 && (
          <div className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
            <div className="p-1.5">
              {suggestions.map((s, i) => {
                const itemType = s.type || (invoice.type === "goods" ? "product" : "service");
                const code = itemType === "product" ? s.hsn : s.sac;
                
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => applyItem(s)}
                    className={`w-full px-3 py-2.5 text-left rounded-md transition-colors ${
                      highlighted === i 
                        ? "bg-indigo-50 border border-indigo-200" 
                        : "hover:bg-slate-50 border border-transparent"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-900">{s.name}</div>
                      <div className="text-sm font-semibold text-slate-700 tabular-nums">
                        {formatCurrency(Number(s.price))}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1">
                        <span className="text-[10px] font-medium text-slate-400">GST</span>
                        {s.tax ?? 0}%
                      </span>
                      {code && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="inline-flex items-center gap-1">
                            <span className="text-[10px] font-medium text-slate-400">
                              {itemType === "product" ? "HSN" : "SAC"}
                            </span>
                            {code}
                          </span>
                        </>
                      )}
                      {s.description && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="truncate">{s.description.substring(0, 30)}</span>
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1.5 block">
          Description
        </label>
        <textarea
          value={item.description || ""}
          onChange={(e) => updateItem(index, { description: e.target.value })}
          placeholder="Add item description (optional)"
          rows={2}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 resize-none transition-all placeholder:text-slate-400 disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
        />
      </div>

      {/* Metadata Row: Type + HSN/SAC + UoM + Billing Frequency */}
      <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-3 items-end">
        {/* Item Type Tag (Non-interactive) */}
        <div>
          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1.5 block">
            Type
          </label>
          <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200">
            <span className="text-xs font-medium text-slate-700">
              {itemType === "product" ? "Product" : "Service"}
            </span>
          </div>
        </div>

        {/* HSN/SAC */}
        {itemType === "product" ? (
          <Field label="HSN Code">
            <input
              type="text"
              value={item.hsn || ""}
              onChange={(e) => updateItem(index, { hsn: e.target.value })}
              placeholder="e.g. 8471"
              disabled={disabled}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
            />
          </Field>
        ) : (
          <Field label="SAC Code">
            <input
              type="text"
              value={item.sac || ""}
              onChange={(e) => updateItem(index, { sac: e.target.value })}
              placeholder="e.g. 998314"
              disabled={disabled}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
            />
          </Field>
        )}

        {/* UoM - Conditional based on item type */}
        <Field label="Unit">
          <select
            value={item.uom || ""}
            onChange={(e) => updateItem(index, { uom: e.target.value })}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all appearance-none cursor-pointer disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
            style={{
              backgroundImage: disabled ? 'none' : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              paddingRight: '2.5rem',
            }}
          >
            <option value="">Select unit</option>
            {itemType === "product" ? (
              // Measurement units for products
              <>
                <option value="pcs">Pieces (pcs)</option>
                <option value="nos">Numbers (nos)</option>
                <option value="kg">Kilograms (kg)</option>
                <option value="gm">Grams (gm)</option>
                <option value="ltr">Liters (ltr)</option>
                <option value="ml">Milliliters (ml)</option>
                <option value="mtr">Meters (mtr)</option>
                <option value="ft">Feet (ft)</option>
                <option value="sqft">Square Feet (sqft)</option>
                <option value="box">Box</option>
                <option value="pack">Pack</option>
                <option value="set">Set</option>
                <option value="unit">Unit</option>
              </>
            ) : (
              // Work units + Billing frequency for services
              <>
                <option value="hrs">Hour</option>
                <option value="days">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
                <option value="task">Task</option>
                <option value="project">Project</option>
                <option value="session">Session</option>
                <option value="unit">Unit</option>
              </>
            )}
          </select>
        </Field>

        {/* Billing Frequency - Only for services */}
        {itemType === "service" && (
          <Field label="Billing">
            <select
              value={item.billingFrequency || "one_time"}
              onChange={(e) => updateItem(index, { billingFrequency: e.target.value })}
              disabled={disabled}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all appearance-none cursor-pointer disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
              style={{
                backgroundImage: disabled ? 'none' : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                paddingRight: '2.5rem',
              }}
            >
              <option value="one_time">One-time</option>
              <option value="per_hour">Per Hour</option>
              <option value="per_day">Per Day</option>
              <option value="per_week">Per Week</option>
              <option value="per_month">Per Month</option>
              <option value="per_quarter">Per Quarter</option>
              <option value="per_year">Per Year</option>
            </select>
          </Field>
        )}
      </div>

      {/* Pricing Grid - Reduced Visual Weight */}
      <div className="bg-gradient-to-br from-slate-50/50 to-slate-50/30 rounded-lg p-5 space-y-4">
        <div className="grid grid-cols-[100px_120px_120px_110px_1fr] gap-4 items-end">
          <Field label="Quantity">
            <QtyInput
              value={item.quantity}
              onChange={(v) => updateItem(index, { quantity: v })}
              disabled={disabled}
            />
          </Field>

          <Field label="Rate">
            <CurrencyInput
              value={item.rate}
              onChange={(v) => updateItem(index, { rate: v })}
              disabled={disabled}
            />
          </Field>

          <Field label="Discount">
            <DiscountInput
              value={item.discount}
              type={item.discountType}
              onChange={(v) => updateItem(index, { discount: v })}
              onTypeChange={(t) => updateItem(index, { discountType: t })}
              disabled={disabled}
            />
          </Field>

          <Field label="GST Rate">
            <TaxPill
              value={item.gstRate}
              onChange={(v) => updateItem(index, { gstRate: v })}
              disabled={disabled}
            />
          </Field>

          <div className="text-right">
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
              Line Total
            </div>
            <div className="text-xl font-semibold text-slate-900 tabular-nums">
              {formatCurrency(totalAmount)}
            </div>
          </div>
        </div>

        {/* Catalog Override Warning */}
        {item.sourceItemId && item.isOverridden && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-md border border-amber-100">
              <Edit3 size={14} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Invoice-only changes</div>
                <div className="text-amber-600 mt-0.5">
                  Rate or tax modifications apply only to this invoice. Catalog item remains unchanged.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* GST Breakdown - Collapsible */}
      {gstRate > 0 && (
        <details className="group bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
          <summary className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-100 transition-colors">
            <div className="flex items-center gap-2">
              <ChevronDown size={14} className="text-slate-400 transition-transform group-open:rotate-180" />
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                GST Breakdown
              </span>
            </div>
            <div className="text-sm font-semibold text-slate-700 tabular-nums">
              {formatCurrency(taxAmount)}
            </div>
          </summary>
          <div className="px-4 pb-3 pt-1 space-y-2">
            <div className="flex justify-between text-sm">
              {gstType === "cgst_sgst" ? (
                <>
                  <span className="text-slate-600">CGST ({gstRate / 2}%)</span>
                  <span className="font-medium text-slate-700 tabular-nums">
                    {formatCurrency(cgstAmount)}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-slate-600">IGST ({gstRate}%)</span>
                  <span className="font-medium text-slate-700 tabular-nums">
                    {formatCurrency(igstAmount)}
                  </span>
                </>
              )}
            </div>
            {gstType === "cgst_sgst" && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">SGST ({gstRate / 2}%)</span>
                <span className="font-medium text-slate-700 tabular-nums">
                  {formatCurrency(sgstAmount)}
                </span>
              </div>
            )}
            <div className="text-[10px] text-slate-500 pt-2 border-t border-slate-200">
              {isSameState
                ? `Same state transaction (${clientState}): CGST + SGST applied`
                : `Inter-state transaction: IGST applied`}
            </div>
          </div>
        </details>
      )}

      {/* Footer Actions - ENHANCED */}
      <div className="flex justify-between items-center pt-3 border-t border-slate-200">
        <div className="flex items-center gap-3">
          <button 
            onClick={onCollapse} 
            className="px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-800 rounded-lg transition-all duration-150 active:scale-95 hover:shadow-sm border border-emerald-200/50"
          >
            {disabled ? 'Close' : '✓ Done'}
          </button>
          {!disabled && item.isNew && (
            <button
              onClick={async () => {
                try {
                  // Save item to product catalog
                  const { itemService } = await import('../../../services/item.service');
                  await itemService.add({
                    type: itemType,
                    name: item.name,
                    description: item.description || '',
                    hsn: item.hsn || '',
                    sac: item.sac || '',
                    uom: item.uom || '',
                    billingFrequency: item.billingFrequency || null,
                    price: item.rate || 0,
                    tax: item.gstRate || 0,
                    notes: '',
                  });
                  
                  // Update item to mark it as saved
                  updateItem(index, { isNew: false, sourceItemId: 'saved' });
                  
                  // Show success message (you can add toast notification here)
                  console.log('Item saved to product catalog');
                } catch (err) {
                  console.error('Failed to save item:', err);
                  alert('Failed to save item to catalog');
                }
              }}
              className="px-4 py-2 text-sm font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-800 rounded-lg transition-all duration-150 active:scale-95 hover:shadow-sm border border-indigo-200/50 flex items-center gap-1.5"
            >
              <Plus size={14} />
              Save as Product
            </button>
          )}
          {!disabled && <ItemSaveStatus item={item} />}
        </div>
        {!disabled && (
          <button 
            onClick={removeItem} 
            className="px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 rounded-lg transition-all duration-150 flex items-center gap-1.5 active:scale-95 hover:shadow-sm border border-red-200/50"
          >
            <Trash2 size={14} />
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────── */

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ onAdd, showError = false }) {
  return (
    <div className="space-y-2">
      <button
        onClick={onAdd}
        className={`w-full border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 group ${
          showError 
            ? 'border-red-200 bg-gradient-to-br from-red-50/50 to-red-50/30 hover:from-red-50/70 hover:to-red-50/50' 
            : 'border-slate-200 bg-gradient-to-br from-slate-50/50 to-slate-50/30 hover:from-slate-50/70 hover:to-slate-50/50 hover:border-indigo-300 hover:shadow-md hover:-translate-y-[1px]'
        }`}
      >
        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 transition-all duration-200 group-hover:scale-110 ${
          showError ? 'bg-red-100 group-hover:bg-red-200' : 'bg-slate-100 group-hover:bg-indigo-100'
        }`}>
          <Plus size={24} className={`transition-colors duration-200 ${
            showError ? 'text-red-500' : 'text-slate-400 group-hover:text-indigo-600'
          }`} />
        </div>
        <div className={`text-sm font-semibold mb-1 transition-colors duration-200 ${
          showError ? 'text-red-700' : 'text-slate-900 group-hover:text-indigo-900'
        }`}>
          {showError ? 'No items yet' : 'Add your first line item'}
        </div>
        <div className="text-xs text-slate-500 mb-4">
          {showError ? 'Add at least one line item to send this invoice' : 'Click to get started with products or services'}
        </div>
        <div className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-150 shadow-sm group-hover:shadow-md group-active:scale-95 ${
          showError 
            ? 'bg-red-600 group-hover:bg-red-700' 
            : 'bg-indigo-600 group-hover:bg-indigo-700'
        }`}>
          <Plus size={16} />
          Add first item
        </div>
      </button>
      
      {/* Inline Error Message */}
      {showError && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 animate-in fade-in slide-in-from-top-1 duration-200">
          <span className="text-red-600 text-sm">⚠</span>
          <p className="text-xs text-red-700">
            Add at least one line item. Invoices need products or services to bill for.
          </p>
        </div>
      )}
    </div>
  );
}

function QtyInput({ value, onChange, disabled = false }) {
  const handleChange = (e) => {
    if (disabled) return;
    const val = e.target.value;
    // Only allow positive integers
    if (val === '' || /^\d+$/.test(val)) {
      const num = val === '' ? 1 : Math.max(1, parseInt(val, 10));
      onChange(num);
    }
  };

  const handleKeyPress = (e) => {
    // Block non-numeric characters
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden w-20">
      <button
        type="button"
        onClick={() => !disabled && onChange(Math.max(1, value - 1))}
        disabled={disabled}
        className="px-1.5 py-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-base flex-shrink-0 active:scale-90"
        title="Decrease quantity"
      >
        &#8722;
      </button>

      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        className="flex-1 text-center text-sm font-medium bg-transparent border-0 focus:outline-none focus:ring-0 tabular-nums disabled:cursor-not-allowed min-w-0 transition-colors duration-150"
      />

      <button
        type="button"
        onClick={() => !disabled && onChange(value + 1)}
        disabled={disabled}
        className="px-1.5 py-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-base flex-shrink-0 active:scale-90"
        title="Increase quantity"
      >
        &#43;
      </button>
    </div>
  );
}

function CurrencyInput({ value, onChange, disabled = false }) {
  const handleChange = (e) => {
    if (disabled) return;
    const val = e.target.value;
    // Allow empty, numbers, and decimal point
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      const num = val === '' ? 0 : Math.max(0, parseFloat(val) || 0);
      onChange(num);
    }
  };

  const handleKeyPress = (e) => {
    // Allow numbers and decimal point only
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
    }
    // Prevent multiple decimal points
    if (e.key === '.' && e.target.value.includes('.')) {
      e.preventDefault();
    }
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">
        ₹
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        className="w-full pl-7 pr-3 py-2 text-sm font-medium text-right bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all tabular-nums disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
        placeholder="0.00"
      />
    </div>
  );
}

function DiscountInput({ value, type, onChange, onTypeChange, disabled = false }) {
  const handleChange = (e) => {
    if (disabled) return;
    const val = e.target.value;
    // Allow empty, numbers, and decimal point
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      let num = val === '' ? 0 : parseFloat(val) || 0;
      // Limit percentage to 100
      if (type === 'percentage') {
        num = Math.min(100, Math.max(0, num));
      } else {
        num = Math.max(0, num);
      }
      onChange(num);
    }
  };

  const handleKeyPress = (e) => {
    // Allow numbers and decimal point only
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
    }
    // Prevent multiple decimal points
    if (e.key === '.' && e.target.value.includes('.')) {
      e.preventDefault();
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          className="w-full pr-8 pl-3 py-2 text-sm font-medium text-right bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all tabular-nums disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
          placeholder="0"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium pointer-events-none">
          {type === "percentage" ? "%" : "₹"}
        </span>
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => !disabled && onTypeChange("percentage")}
          disabled={disabled}
          className={`flex-1 px-2 py-1 text-[10px] font-medium rounded-md transition-all ${
            type === "percentage"
              ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
              : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          Percent
        </button>
        <button
          type="button"
          onClick={() => !disabled && onTypeChange("flat")}
          disabled={disabled}
          className={`flex-1 px-2 py-1 text-[10px] font-medium rounded-md transition-all ${
            type === "flat"
              ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
              : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          Fixed
        </button>
      </div>
    </div>
  );
}

/* ───────── Save Status Indicator ───────── */

function ItemSaveStatus({ item }) {
  const hasChanges = item.isNew || item.isOverridden;

  if (!hasChanges) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-100">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
        <span className="text-[10px] font-medium text-emerald-700">From catalog</span>
      </div>
    );
  }

  if (item.isNew) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-200">
        <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div>
        <span className="text-[10px] font-medium text-slate-600">Not saved to catalog</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-100">
      <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
      <span className="text-[10px] font-medium text-amber-700">Invoice-only changes</span>
    </div>
  );
}

/* ───────── Helper Functions ───────── */

function formatBillingFrequency(frequency) {
  const frequencyMap = {
    'one_time': 'one-time',
    'per_hour': 'hour',
    'per_day': 'day',
    'per_week': 'week',
    'per_month': 'month',
    'per_quarter': 'quarter',
    'per_year': 'year',
  };
  return frequencyMap[frequency] || frequency;
}
